import { MainMetaMapSubTopic, MainMetaMapTopic } from "@tsTypes/meta-schema";
import { Resources } from "@tsTypes/subject-schema";
import { ReduxResourceType } from "@tsTypes/user-metadata-schema";
import { capFirst } from "../strapi/formatters";

const MATHS_EXCLUDED_RESOURCES: Resources[] = ["videos", "ia examples"];
const SUBJECT_ALLOWED_RESOURCES: Resources[] = [
  "videos",
  "notes",
  "definitions",
  "flashcards",
  "questions",
  "cram sheets",
  "topic tests",
  "individual topic test",
  "test taker",
  "mock exams",
  "test builder",
  "command terms",
  "exam breakdown",
  "ia examples",
  "ia criteria",
  "syllabus",
  "exam tests",
];
const TOPIC_BANNER_RESOURCES: Resources[] = [
  "videos",
  "notes",
  "definitions",
  "flashcards",
  "questions",
  "cram sheets",
  "topic tests",
  "mock exams",
  "test builder",
];
const USEFUL_BANNER_RESOURCES: Resources[] = [
  "syllabus",
  "exam breakdown",
  "ia examples",
  "ia criteria",
  "command terms",
  "test taker",
];

const NEXT_LINKED_RESOURCE_MAP: Partial<Record<Resources, Resources>> = {
  videos: "notes",
  notes: "flashcards",
  flashcards: "questions",
  questions: "cram sheets",
  definitions: "videos",
  "cram sheets": "topic tests",
};

// **************************************************************************************** TITLES

export function parseSubjectUrl(url: string) {
  return capFirst(
    urlToTitle(url).replace(/(\sa[ai])/, (match, p1) => p1.toUpperCase())
  );
}

export function stripSubjectFromYear(fullName: string) {
  return fullName.replace(" 2025", "");
}

export function titleToUrl(title: string) {
  return encodeURIComponent(
    title
      .toLowerCase()
      .replaceAll(", &", "_ &") // fix for oxford commma with &
      .replaceAll(", ", "_")
      .replaceAll(" & ", "-and-")
      .replaceAll(" ", "-")
  );
}

export function urlToTitle(url: string) {
  return decodeURIComponent(
    url
      .replaceAll("_-", ",-") // fix for oxford commma with &
      .replaceAll("_", ", ")
      .replaceAll("-and-", " & ")
      .replaceAll("-", " ")
  );
}

// **************************************************************************************** PATHS

export function getLoginUrl() {
  let callbackUrl = "/profile";
  if (typeof window === "undefined") {
    return "/login";
  }
  if (window.location.pathname.startsWith(`/ib/`)) {
    callbackUrl = window.location.pathname;
  }
  return "/login?callbackUrl=" + encodeURIComponent(callbackUrl);
}

export function getIBOSubjectLink(subjectName: string) {
  const parsedName = stripSubjectFromYear(subjectName.toLowerCase());
  let iboRef = "#";
  switch (parsedName) {
    case "biology":
    case "chemistry":
    case "physics":
      iboRef = `https://www.ibo.org/programmes/diploma-programme/curriculum/sciences/${parsedName}/`;
      break;
    case "maths aa":
    case "maths ai":
      iboRef = `https://www.ibo.org/programmes/diploma-programme/curriculum/mathematics/`;
  }
  return iboRef;
}

export function buildSubjectSlug(subject: string) {
  return `/ib/${titleToUrl(subject)}`;
}

export function buildTopicSlug(
  subject: string,
  modifiedTopicNumber: string,
  topicTitle: string
) {
  return `ib-${titleToUrl(subject)}-topic-${titleToUrl(
    modifiedTopicNumber
  )}-${titleToUrl(topicTitle)}`;
}

function buildSubTopicSlug(subTopicTitle: string) {
  return `${titleToUrl(subTopicTitle)}`;
}

function buildResourceSlug(resource: Resources | "all") {
  switch (resource) {
    case "all":
      return "";
    case "videos":
    case "notes":
    case "definitions":
    case "flashcards":
    case "questions":
    case "cram sheets":
    case "test builder":
    case "test taker":
    case "mock exams":
    case "exam tests":
    case "topic tests":
    case "syllabus":
      return `${titleToUrl(resource)}`;
    case "exam breakdown":
    case "command terms":
      return `useful-resources/${titleToUrl(resource)}`;
    case "ia examples":
    case "ia criteria":
      return `useful-resources/internal-assessment/${titleToUrl(resource)}`;
    case "individual topic test":
      return `topic-tests`;
  }
}

export function buildFullPath({
  subject,
  resource,
  topicNumber,
  topicTitle,
  subTopicTitle,
}: BuildFullPathParams) {
  return `${buildSubjectSlug(subject)}/${buildResourceSlug(resource)}${
    topicTitle &&
    !["mock exams","test builder","test taker"].includes(resource)
     ? `/${buildTopicSlug(subject, topicNumber, topicTitle)}` : ""
  }${
    topicTitle &&
    subTopicTitle &&
    !["videos", "cram sheets", "topic tests","mock exams","test builder","test taker"].includes(resource)
      ? `/${buildSubTopicSlug(subTopicTitle)}`
      : ""
  }`;
}

export function isMathsSubject(subjectNameLower: string): boolean {
  return subjectNameLower.includes("maths");
}

export function getAllowedResourcesForSubject(
  subjectNameLower: string
): Resources[] {
  if (!isMathsSubject(subjectNameLower)) {
    return SUBJECT_ALLOWED_RESOURCES;
  }
  return SUBJECT_ALLOWED_RESOURCES.filter(
    (resource) => !MATHS_EXCLUDED_RESOURCES.includes(resource)
  );
}

export function isResourceAllowedForSubject(
  subjectNameLower: string,
  resource: Resources
) {
  return getAllowedResourcesForSubject(subjectNameLower).includes(resource);
}

export function getNextLinkedResourceForSubject(
  subjectNameLower: string,
  currentResource: Resources
): Resources | null {
  const nextResource = NEXT_LINKED_RESOURCE_MAP[currentResource];
  if (!nextResource) {
    return null;
  }
  return isResourceAllowedForSubject(subjectNameLower, nextResource)
    ? nextResource
    : null;
}

export type BannerLinkItem = {
  label: string;
  href: string;
  badge?: "Suggested";
  resource?: Resources | "all";
};

type BannerLinkContext =
  | {
      context: "useful";
      subjectNameLower: string;
      currentResource: Resources;
    }
  | {
      context: "topic";
      subjectNameLower: string;
      currentResource: Resources;
      topicNumber: string;
      topicTitle: string;
    }
  | {
      context: "subtopic";
      subjectNameLower: string;
      currentResource: Resources;
      topicNumber: string;
      topicTitle: string;
      subTopicTitle: string;
    };

export function getBannerLinksForContext(
  params: BannerLinkContext
): BannerLinkItem[] {
  const allowedResources = getAllowedResourcesForSubject(params.subjectNameLower);
  const links: BannerLinkItem[] = [];

  if (params.context === "useful") {
    USEFUL_BANNER_RESOURCES.filter(
      (resource) =>
        resource !== params.currentResource && allowedResources.includes(resource)
    ).forEach((resource) => {
      links.push({
        label: getResourceDisplayName(resource),
        href: buildFullPath({
          subject: params.subjectNameLower,
          resource,
        }),
        resource,
      });
    });
  } else {
    const contextResources = TOPIC_BANNER_RESOURCES.filter(
      (resource) =>
        resource !== params.currentResource && allowedResources.includes(resource)
    );
    contextResources.forEach((resource) => {
      links.push({
        label: getResourceDisplayName(resource),
        href: buildFullPath({
          subject: params.subjectNameLower,
          resource,
          topicNumber: params.topicNumber,
          topicTitle: params.topicTitle,
          subTopicTitle:
            params.context === "subtopic" ? params.subTopicTitle : undefined,
        }),
        resource,
      });
    });
  }

  links.push({
    label: "All Resources",
    href: buildSubjectSlug(params.subjectNameLower),
    resource: "all",
  });

  return links;
}

export function getResourceDisplayName(resource: Resources | "all") {
  switch (resource) {
    case "all":
      return "All resources";
    case "ia examples":
      return "IA examples";
    case "ia criteria":
      return "IA criteria";
    case "exam breakdown":
      return "Exam breakdown";
    case "command terms":
      return "Command terms";
    default:
      return capFirst(resource);
  }
}

// Titles
export function getTopicTitleWithHLIndicator({
  title,
  isHL,
}: Pick<MainMetaMapTopic, "title" | "isHL">) {
  return `${title}${isHL ? " (HL)" : ""}`;
}

export function getSubTopicTitleWithHLIndicator({
  title,
  isHL,
}: Pick<MainMetaMapSubTopic, "title" | "isHL">) {
  return `${title}${isHL ? " (HL)" : ""}`;
}

export function getUserPreferredSubTopicTitle(
  subTopicMeta: Pick<MainMetaMapSubTopic, "label" | "title" | "isHL">
) {
  return `${subTopicMeta.label}: ${getSubTopicTitleWithHLIndicator(
    subTopicMeta
  )}`;
}

// redux
export function convertReduxResourceTypeToResource(
  resource: ReduxResourceType
): Resources {
  switch (resource) {
    case "videos":
      return "videos";
    case "notes":
      return "notes";
    case "flashcards":
      return "flashcards";
    case "questions":
      return "questions";
    case "definitions":
      return "definitions";
    case "cramSheets":
      return "cram sheets";
    case "customTests":
      return "test taker";
    case "topicTests":
      return "topic tests";
    case "mockExams":
      return "mock exams";
  }
}

type BuildFullPathParams =
  | {
      subject: string;
      resource: Resources | "all";
      topicNumber?: undefined;
      topicTitle?: undefined;
      subTopicTitle?: undefined;
    }
  | {
      subject: string;
      resource: Resources | "all";
      topicNumber: string;
      topicTitle: string;
      subTopicTitle?: string;
    };
